import { DEFAULT_OPACITY } from "../../shared/constants";
import AbstractShape from "./AbstractShape";
import Coordinate from "../support/Coordinate";
import RectCoordinate from "../support/RectCoordinate";
import RectangleVerticalBorder from "../support/RectangleVerticalBorder";
import RectangleHorizontalBorder from "../support/RectangleHorizontalBorder";
import RotateCoordinate from "../support/RotateCoordinate";

export default class Rectangle extends AbstractShape {
    /**
     * Rectangles are drawn with the center as the origin.
     * This is done to enable easy rotation in the future.
     * It does make it a little more difficult to reshape the rectangle,
     * as can be seen in the setWidth and setHeight methods and when moving
     * corners.
     */
    corners = null;
    center = null;
    rotation = 0;
    type = 'rectangle';

    constructor(provider, data, appearance, speciesIds, label) {
        super(provider, data, appearance, speciesIds, label);

        this.center = new Coordinate(this.provider, data.x, data.y);
        this.rotation = data.rotation || 0;
        this.initCorners(data.width / 2, data.height / 2);
        this.initBorders();
        this.initRotation();
    }

    clone(dx, dy) {
        return new Rectangle(
            this.provider,
            {
                x: this.absX + dx,
                y: this.absY + dy,
                width: this.width,
                height: this.height,
                rotation: this.rotation,
            },
            this.appearance.clone(),
            this.speciesIds,
            this.label.clone(),
        )
    }

    /**
     * The users draws the rectangle by dragging from one corner to the other.
     * But we store the rectangle as a center and a width and height. This
     * method converts the two corners to a center and width and height.
     */
    static createFromCorners(provider, coord1, coord2, appearance, speciesIds = [], label) {
        let rWidth = coord2.absX - coord1.absX;
        let rHeight = coord2.absY - coord1.absY;
        return new Rectangle(
            provider,
            {
                x: coord1.absX + rWidth / 2,
                y: coord1.absY + rHeight / 2,
                width: provider.p.abs(rWidth),
                height: provider.p.abs(rHeight),
            },
            appearance,
            speciesIds,
            label,
        );
    }

    display() {
        this.appearance.apply(this.isHovered, this.selected, this.showFlash, this.p.map(this.currentMaxSize, 500, 1500, DEFAULT_OPACITY, 50, true));

        this.p.rectMode(this.p.CENTER);

        this.p.push();
        this.p.translate(this.currentX, this.currentY);
        this.p.rotate(this.rotation);
        this.p.rect(0, 0, this.currentWidth, this.currentHeight);

        if (this.editMode && this.selected && this.selection.length == 1) {
            this.corners.map((c) => c.display());
            this.rotate.display();
        }
        this.p.pop();

        this.label.display(this.currentMaxSize, this.rotation);
    }

    mouseOver(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        // not selected
        if (!this.selected) {
            if (this.hovered(viewX, viewY)) {
                this.cursor.pointer();
                return true;
            }
            return false;
        }

        // more elements selected
        if (this.selection.length > 1) {
            if (this.hovered(viewX, viewY)) {
                this.cursor.grab();
                return true;
            }
            return false;
        }

        // single element selected
        if (this.rotate.mouseOver(viewX, viewY)) {
            return true;
        }

        if (this.corners.find((c) => c.mouseOver(viewX, viewY))) {
            return true;
        }

        if (this.borders.find((b) => b.mouseOver(viewX, viewY))) {
            return true;
        }

        if (this.label.mouseOver(viewX, viewY)) {
            return true;
        }

        if (this.hovered(viewX, viewY)) {
            this.cursor.grab();
            return true;
        }
        return false;
    }

    move(dx, dy) {
        this.center.move(dx, dy);
        // this.corners.map((c) => c.move(dx, dy, false));
        // this.rotate.move(dx, dy, false);
    }

    hovered(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return -this.currentWidth / 2 <= viewX
            && this.currentWidth / 2 >= viewX
            && -this.currentHeight / 2 <= viewY
            && this.currentHeight / 2 >= viewY;
    }

    hoveredCorners(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return this.corners.find((c) => c.hovered(viewX, viewY));
    }

    hoveredBorders(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return this.borders.find((b) => b.hovered(viewX, viewY));
    }

    hoveredWithSupport(viewX, viewY) {
        [viewX, viewY] = this.ensureViewXandY(viewX, viewY);
        return (this.rotate.hovered(viewX, viewY) ? this.rotate : null)
            || this.hoveredCorners(viewX, viewY)
            || this.hoveredBorders(viewX, viewY)
            || (this.label.hovered(viewX, viewY) ? this.label : null)
            || (this.hovered(viewX, viewY) ? this : null);
    }

    surrounded(minX, minY, maxX, maxY) {
        return this.absMinX >= minX
            && this.absMaxX <= maxX
            && this.absMinY >= minY
            && this.absMaxY <= maxY;
    }

    initCorners(halfWidth, halfHeight) {
        this.corners = [
            new RectCoordinate(this.provider, -halfWidth, -halfHeight, this), // top left
            new RectCoordinate(this.provider, halfWidth, halfHeight, this), // bottom right
            new RectCoordinate(this.provider, -halfWidth, halfHeight, this), // bottom left
            new RectCoordinate(this.provider, halfWidth, -halfHeight, this), // top right
        ];
    }

    updateCorners() {
        let halfWidth = this.width / 2;
        let halfHeight = this.height / 2;

        // We have to first retrieve the corners, before changing
        // the values, because changing the values will change which
        // corners are returned.
        let rightCorners = this.rightCorners;
        let leftCorners = this.leftCorners;
        let bottomCorners = this.bottomCorners;
        let topCorners = this.topCorners;

        rightCorners.map(c => c.absX = halfWidth);
        leftCorners.map(c => c.absX = -halfWidth);
        bottomCorners.map(c => c.absY = halfHeight);
        topCorners.map(c => c.absY = -halfHeight);
    }

    initBorders() {
        this.borders = [
            new RectangleVerticalBorder(this.provider, this, this.corners[0], this.corners[2]), // left
            new RectangleVerticalBorder(this.provider, this, this.corners[1], this.corners[3]), // right
            new RectangleHorizontalBorder(this.provider, this, this.corners[0], this.corners[3]), // top
            new RectangleHorizontalBorder(this.provider, this, this.corners[1], this.corners[2]), // bottom
        ];

        this.borders[0].setOpposite(this.borders[1]);
        this.borders[1].setOpposite(this.borders[0]);
        this.borders[2].setOpposite(this.borders[3]);
        this.borders[3].setOpposite(this.borders[2]);

        this.corners[0].setRelations(this.borders[2], this.borders[0]);
        this.corners[1].setRelations(this.borders[3], this.borders[1]);
        this.corners[2].setRelations(this.borders[3], this.borders[0]);
        this.corners[3].setRelations(this.borders[2], this.borders[1]);
    }

    initRotation() {
        this.rotate = new RotateCoordinate(this.provider, this, this.width / 2 + 60, 0);
    }

    setWidth(width) {
        let dwidth = width - this.width;
        this.absX += (dwidth / 2) * Math.cos(this.rotation);
        this.absY += (dwidth / 2) * Math.sin(this.rotation);
        this.rightCorners.map(c => c.absX += dwidth);
        this.updateCorners();
        this.row.updateImg();
    }

    setHeight(height) {
        let dheight = height - this.height;
        this.absX += (dheight / 2) * Math.cos(this.rotation + Math.PI / 2);
        this.absY += (dheight / 2) * Math.sin(this.rotation + Math.PI / 2);
        this.bottomCorners.map(c => c.absY += dheight);
        this.updateCorners();
        this.row.updateImg();
    }

    export() {
        return {
            type: 'rectangle',
            data: {
                x: this.absX,
                y: this.absY,
                width: this.width,
                height: this.height,
                notes: this.notes,
                rotation: this.rotation,
                name: this._name,
                ...this.appearance.export(),
                ...this.label.export(),
            },
            speciesIds: this.speciesIds,
        }
    }

    getMiniView(max) {
        let maxSize = this.p.max(this.width, this.height);
        return [
            this.p.map(this.width, 0, maxSize, 0, max),
            this.p.map(this.height, 0, maxSize, 0, max),
        ];
    }

    ensureViewXandY(viewX, viewY) {
        if (viewX === undefined || viewY === undefined) {
            return this.view.getRelative(this.currentX, this.currentY, this.rotation);
        }
        return [viewX, viewY];
    }

    get width() {
        return Math.abs(this.corners[0].absX - this.corners[1].absX);
    }

    get height() {
        return Math.abs(this.corners[0].absY - this.corners[1].absY);
    }

    set absX(value) {
        this.center.absX = value;
    }

    set absY(value) {
        this.center.absY = value;
    }

    get absX() {
        return this.center.absX;
    }

    get absY() {
        return this.center.absY;
    }

    get currentX() {
        return this.center.currentX;
    }

    get currentY() {
        return this.center.currentY;
    }

    get currentWidth() {
        return this.width * this.view.scale;
    }

    get currentHeight() {
        return this.height * this.view.scale;
    }

    get displayWidth() {
        return this.calculatePixelToDisplayDistance(this.width);
    }

    get displayHeight() {
        return this.calculatePixelToDisplayDistance(this.height);
    }

    get absMinX() {
        return this.absX - this.width / 2;
    }

    get absMaxX() {
        return this.absX + this.width / 2;
    }

    get absMinY() {
        return this.absY - this.height / 2;
    }

    get absMaxY() {
        return this.absY + this.height / 2;
    }

    get currentMinX() {
        return this.absMinX * this.view.scale;
    }

    get currentMaxX() {
        return this.absMaxX * this.view.scale;
    }

    get currentMinY() {
        return this.absMinY * this.view.scale;
    }

    get currentMaxY() {
        return this.absMaxY * this.view.scale;
    }

    get rightCorners() {
        return this.corners[1].absX > this.corners[0].absX
            ? [this.corners[1], this.corners[3]]
            : [this.corners[0], this.corners[2]];
    }

    get leftCorners() {
        return this.corners[1].absX < this.corners[0].absX
            ? [this.corners[1], this.corners[3]]
            : [this.corners[0], this.corners[2]];
    }

    get bottomCorners() {
        return this.corners[1].absY > this.corners[0].absY
            ? [this.corners[1], this.corners[2]]
            : [this.corners[0], this.corners[3]];
    }

    get topCorners() {
        return this.corners[1].absY < this.corners[0].absY
            ? [this.corners[1], this.corners[2]]
            : [this.corners[0], this.corners[3]];
    }

    get currentMinSize() {
        return this.p.min(this.width, this.height) * this.view.scale;
    }

    get currentMaxSize() {
        return this.p.max(this.width, this.height) * this.view.scale;
    }
}
